import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";

import { CheckboxList, Input, Paragraph } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import { ApiTreatmentPlanTemplateFieldFieldTypeChoices } from "@api/graphql/types-and-hooks";
import { OptionsEditor } from "@components/OptionsEditor/OptionsEditor";
import { TemplateFieldState } from "@pages/Templates/components/TreatmentPlanTemplateEditor/TreatmentPlanTemplateEditor";

const FIELD_TYPE_LABELS: Record<string, string> = {
  [ApiTreatmentPlanTemplateFieldFieldTypeChoices.SimpleText]: "Text",
  [ApiTreatmentPlanTemplateFieldFieldTypeChoices.Wysiwyg]: "Text Area",
  [ApiTreatmentPlanTemplateFieldFieldTypeChoices.Select]: "Dropdown",
  [ApiTreatmentPlanTemplateFieldFieldTypeChoices.Checkbox]: "Checkboxes",
  [ApiTreatmentPlanTemplateFieldFieldTypeChoices.Radio]: "Radio Button",
  [ApiTreatmentPlanTemplateFieldFieldTypeChoices.Date]: "Date",
  [ApiTreatmentPlanTemplateFieldFieldTypeChoices.Datetime]: "Date Time",
};

const FIELD_TYPES_WITH_OPTIONS = new Set([
  ApiTreatmentPlanTemplateFieldFieldTypeChoices.Select,
  ApiTreatmentPlanTemplateFieldFieldTypeChoices.Checkbox,
  ApiTreatmentPlanTemplateFieldFieldTypeChoices.Radio,
]);

function fieldTypeHasOptions(fieldType: string): boolean {
  return FIELD_TYPES_WITH_OPTIONS.has(fieldType as ApiTreatmentPlanTemplateFieldFieldTypeChoices);
}

function parseOptions(content?: string | null): string[] {
  if (!content) return [];
  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed?.options) ? parsed.options : [];
  } catch {
    return [];
  }
}

function buildContentWithOptions(options: string[], fieldType: string): string {
  const isMultiSelect = fieldType === ApiTreatmentPlanTemplateFieldFieldTypeChoices.Checkbox;
  return JSON.stringify({ options, answer: isMultiSelect ? [] : null });
}

interface TreatmentPlanTemplateEditorFieldProps {
  field: TemplateFieldState;
  onUpdate: (field: TemplateFieldState) => void;
  onRemove: () => void;
  readonly: boolean;
}

export const TreatmentPlanTemplateEditorField: React.FC<TreatmentPlanTemplateEditorFieldProps> = ({
  field,
  onUpdate,
  onRemove,
  readonly,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled: readonly,
  });

  const hasOptions = fieldTypeHasOptions(field.fieldType);
  const [isExpanded, setIsExpanded] = useState(hasOptions);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(field.title);

  // Sync local title when field prop changes (e.g. after save/reload)
  useEffect(() => {
    setLocalTitle(field.title);
  }, [field.title]);

  // Auto-expand when switching to a field type that has options
  useEffect(() => {
    if (hasOptions) {
      setIsExpanded(true);
    }
  }, [hasOptions]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  const selectedFieldTypeLabel = FIELD_TYPE_LABELS[field.fieldType] ?? "Text";

  const fieldTypeMenuItems: PopoverMenuItem[] = Object.entries(FIELD_TYPE_LABELS).map(([id, label]) => ({
    id,
    label,
    callback: () => handleFieldTypeChange(id),
  }));

  // Native fields back a real column on ReportGoal/ReportTarget — deleting
  // them from the template would orphan the column, so disallow it in the UI.
  const isNativeField = field.reportGoalField || field.reportTargetField;
  const actionMenuItems: PopoverMenuItem[] = [
    {
      label: "Delete Field",
      Icon: TrashIcon,
      callback: onRemove,
      disabled: isNativeField,
      disableReason: isNativeField ? "Default fields cannot be deleted" : undefined,
    },
  ];

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (localTitle !== field.title) {
      onUpdate({ ...field, title: localTitle });
    }
  };

  const handleFieldTypeChange = (newFieldType: string) => {
    const switchingToOptions = fieldTypeHasOptions(newFieldType);
    const hadOptions = fieldTypeHasOptions(field.fieldType);

    let newContent = field.content;
    if (switchingToOptions) {
      // Preserve existing options but normalize the answer shape for the new type
      const existingOptions = hadOptions ? parseOptions(field.content) : [];
      const options = existingOptions.length > 0 ? existingOptions : ["Option 1", "Option 2"];
      newContent = buildContentWithOptions(options, newFieldType);
    }

    onUpdate({ ...field, fieldType: newFieldType, content: newContent });
  };

  const handleOptionsChange = (newOptions: string[]) => {
    const newContent = buildContentWithOptions(newOptions, field.fieldType);
    onUpdate({ ...field, content: newContent });
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-limestone-200 rounded-lg group/field">
      {/* Header row */}
      <div className="flex items-center gap-4 p-3">
        <div
          {...(readonly ? {} : { ...attributes, ...listeners })}
          className={`shrink-0 ${readonly ? "invisible" : "text-text-secondary cursor-grab"}`}
        >
          <div className="flex w-5">
            <EllipsisVerticalIcon className="h-5 w-5" />
            <EllipsisVerticalIcon className="h-5 w-5 -ml-3" />
          </div>
        </div>
        {hasOptions && (
          <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="shrink-0">
            {isExpanded ? (
              <ChevronUpIcon className="h-3.5 w-3.5 text-text-secondary" />
            ) : (
              <ChevronDownIcon className="h-3.5 w-3.5 text-text-secondary" />
            )}
          </button>
        )}
        <div className="flex items-center flex-1 min-w-0 group/title">
          {isEditingTitle ? (
            <Input
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              autoFocus={true}
              className="[&>div>input]:ring-0 [&>div>input]:shadow-none [&>div>input]:bg-transparent [&>div>input]:py-1 [&>div>input]:text-sm -mb-6"
              placeholder="Field name"
              name={`field-${field.id}`}
              label=""
            />
          ) : (
            <>
              <Paragraph
                colorType="primary"
                className="text-sm! cursor-pointer truncate"
                onClick={(e) => {
                  if (readonly) return;
                  e.stopPropagation();
                  setLocalTitle(field.title);
                  setIsEditingTitle(true);
                }}
              >
                {field.title || <span className="text-text-secondary italic">Field name</span>}
              </Paragraph>
              {!readonly && (
                <PencilSquareIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocalTitle(field.title);
                    setIsEditingTitle(true);
                  }}
                  className="h-5 w-5 ml-1.5 cursor-pointer rounded-2xl bg-limestone-50 hover:bg-limestone-100 p-0.5 text-text-secondary opacity-0 group-hover/title:opacity-100 shrink-0"
                />
              )}
            </>
          )}
        </div>
        <div className="shrink-0">
          <PopoverMenu
            openDirection="to-bottom-left"
            useSelectButton={true}
            label={selectedFieldTypeLabel}
            disabled={readonly}
            menuItems={fieldTypeMenuItems}
          />
        </div>
        <div className="shrink-0 [&>div>fieldset]:mt-0">
          <CheckboxList
            items={[
              { id: "enabled", title: "Enabled", defaultChecked: field.isEnabled },
              { id: "required", title: "Required", defaultChecked: field.isRequired },
            ]}
            title=""
            legend=""
            orientation="horizontal"
            disabled={readonly}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const id = e.target.value;
              if (id === "enabled") {
                onUpdate({ ...field, isEnabled: !field.isEnabled });
              } else if (id === "required") {
                onUpdate({ ...field, isRequired: !field.isRequired });
              }
            }}
            formNoValidate
          />
        </div>
        <div
          className={`shrink-0 ${readonly ? "invisible" : "opacity-0 group-hover/field:opacity-100 duration-200 transition-opacity"}`}
        >
          <PopoverMenu openDirection="to-right" menuItems={actionMenuItems} />
        </div>
      </div>

      {/* Options editor (collapsible) */}
      {hasOptions && isExpanded && (
        <div className="mx-2 mb-2 p-3 bg-limestone-50 rounded-lg border border-limestone-100">
          <OptionsEditor options={parseOptions(field.content)} onChange={handleOptionsChange} readonly={readonly} />
        </div>
      )}
    </div>
  );
};
